import { isString } from "generic-type-guard";
import React, { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DefaultWidgetMap, Tooltips } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { MultiselectProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/multiselect";

import { fetchWrapper } from "../../../app/sagas/fetchSagaFactory";
import { selectEnvironmentData } from "../../../app/slices/environmentData/selectors";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { RESOURCE_KEYS } from "../../../localization";
import { assertNonNullable } from "../../../utilities/assert";
import { isArrayOf } from "../../../utilities/typeGuardUtils";

export function CustomMultiselect(props: Readonly<MultiselectProps>): ReactElement {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);
	const instance = useSelector(ActivitySelectors.activityById(activityId))?.descriptor.instance;
	const engineState = useSelector(FormEngineSelectors.engineState(activityId));
	const currentScreenPath = engineState && UiStateSelectors.currentScreenLocation()(engineState).path;

	if (isBundId(currentScreenPath) && instance) {
		return <PostfachMultiselect {...props} />;
	}

	return <DefaultWidgetMap.MultiSelect {...props} />;
}

function isBundId(currentScreenPath?: EntityInstancePath): boolean {
	return !!currentScreenPath?.some(e => e.elementName === "DokumentVersendenBundId");
}

function PostfachMultiselect(props: Readonly<MultiselectProps>): ReactElement {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);
	const instance = useSelector(ActivitySelectors.activityById(activityId))?.descriptor.instance;
	const [allowedAttachments, setAllowedAttachments] = useState<string[] | undefined | null>(undefined);
	const { localizer } = useContext(LocalizerContext);
	const environmentData = useSelector(selectEnvironmentData);

	useEffect(() => {
		fetchWrapper({
			method: "GET",
			relativeUrl: `/postfach/allowed-attachments?documentReference=${instance}`
		})
			.then(e => e.json())
			.then(e => {
				if (isArrayOf(isString)(e)) {
					setAllowedAttachments(e);
				} else {
					setAllowedAttachments(null);
				}
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // empty on purpose because the call should only be done once

	const items = useMemo(() => {
		if (isMultiselectItems(props.items) && isArrayOf(isString)(allowedAttachments)) {
			return removeForbiddenAttachments(props.items, allowedAttachments);
		} else {
			return props.items;
		}
	}, [props.items, allowedAttachments]);

	const toManyFilesErrorText = localizer({
		key: RESOURCE_KEYS.features.postfach.error.toManyFiles,
		args: {
			maxFiles: {
				type: "plain",
				value: environmentData.postfach?.maxFiles
			}
		}
	});

	const toManyFilesHelperText = localizer({
		key: RESOURCE_KEYS.features.postfach.helper.attachments,
		args: {
			maxFiles: {
				type: "plain",
				value: environmentData.postfach?.maxFiles
			}
		}
	});

	const allowedExtensionsText = React.useMemo(
		() => getAllowedExtensionsText(environmentData.postfach?.allowedExtensions),
		[environmentData.postfach?.allowedExtensions]
	);

	const tooltipText = localizer({
		key: RESOURCE_KEYS.features.postfach.info.attachments,
		args: {
			allowedExtensions: {
				type: "plain",
				value: allowedExtensionsText
			},
			allowedSizeMb: {
				type: "plain",
				value: environmentData.postfach?.allowedSizeMb
			}
		}
	});

	return (
		<DefaultWidgetMap.MultiSelect
			{...props}
			items={items}
			disabled={allowedAttachments === undefined || props.disabled}
			errorMessage={
				isMultiselectItems(items) &&
				items.filter(e => e.selected).length > (environmentData.postfach?.maxFiles ?? 0) &&
				toManyFilesErrorText
			}
			tooltips={<Tooltips infoTooltip={{ id: "info", content: tooltipText }} />}
			helperText={toManyFilesHelperText}
		/>
	);
}

function isMultiselectItems(value: unknown): value is MultiselectProps.Item[] {
	return Array.isArray(value) && value.every(isMultiselectItem);
}

function isMultiselectItem(obj: unknown): obj is MultiselectProps.Item {
	return (
		typeof obj === "object" &&
		obj !== null &&
		"id" in obj &&
		typeof obj.id === "string" &&
		"label" in obj &&
		typeof obj.label === "string"
	);
}

function removeForbiddenAttachments(
	items: MultiselectProps.Item[],
	allowedExtensions: string[]
): MultiselectProps.Item[] {
	return items.filter(item => allowedExtensions.some(allowedExtension => item.id == allowedExtension));
}

function getAllowedExtensionsText(allowedExtensions?: string[]): string | undefined {
	if (!allowedExtensions || allowedExtensions.length === 0) {
		return "";
	}

	if (allowedExtensions.length === 1) {
		return allowedExtensions[0];
	}

	return (
		allowedExtensions.slice(0, allowedExtensions.length - 1).join(",  ") +
		" und " +
		allowedExtensions[allowedExtensions.length - 1]
	);
}
